import React from 'react';
import { Bell, BellOff, Send, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import usePushNotifications from '../hooks/usePushNotifications';

export default function NotificationSettings({ compact = false }) {
  const {
    permission,
    isSubscribed,
    isSupported,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const [testSent, setTestSent] = React.useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const handleTest = async () => {
    const result = await sendTestNotification();
    if (result.success) {
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    }
  };

  if (!isSupported) {
    if (compact) return null;
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-700">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">Push notifications are not supported in this browser.</p>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`flex items-center gap-2 w-full px-4 py-2 text-left text-sm hover:bg-gray-100 rounded-lg transition ${
          isSubscribed ? 'text-green-600' : 'text-gray-600'
        }`}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isSubscribed ? (
          <Bell className="w-4 h-4" />
        ) : (
          <BellOff className="w-4 h-4" />
        )}
        <span>{isSubscribed ? 'Notifications On' : 'Enable Notifications'}</span>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Bell className="w-5 h-5" />
        Push Notifications
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {permission === 'denied' && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-700">
            Notifications are blocked. Please enable them in your browser settings.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">
              {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
            </p>
            <p className="text-sm text-gray-500">
              {isSubscribed 
                ? 'You will receive push notifications for announcements, messages, and updates.'
                : 'Enable to receive instant updates about your Chiefs family.'}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={isLoading || permission === 'denied'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isSubscribed ? 'bg-green-500' : 'bg-gray-300'
            } ${isLoading || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isSubscribed ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
            {isLoading && (
              <Loader2 className="absolute inset-0 m-auto w-4 h-4 animate-spin text-white" />
            )}
          </button>
        </div>

        {isSubscribed && (
          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={handleTest}
              disabled={isLoading || testSent}
              className="flex items-center gap-2 px-4 py-2 bg-[#32425c] text-white rounded-lg hover:bg-[#263347] transition text-sm font-medium disabled:opacity-50"
            >
              {testSent ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Notification Sent!
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Test Notification
                </>
              )}
            </button>
          </div>
        )}

        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            You'll be notified about:
          </p>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">
            <li>• New announcements from admins</li>
            <li>• Direct messages</li>
            <li>• Payment confirmations</li>
            <li>• Registration updates</li>
            <li>• Task reminders (for admins)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
